window.VAE = window.VAE || {};

// Simple Event System for VAE
VAE.events = (function() {
  const listeners = {};
  
  return {
    on: function(event, callback) {
      if (!listeners[event]) {
        listeners[event] = [];
      }
      listeners[event].push(callback);
    },
    
    off: function(event, callback) {
      if (!listeners[event]) return;
      const index = listeners[event].indexOf(callback);
      if (index > -1) {
        listeners[event].splice(index, 1);
      }
    },
    
    trigger: function(event, data) {
      if (!listeners[event]) return;
      listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('VAE.events: Error in event callback:', error);
        }
      });
    }
  };
})();

VAE.Core = (function () {
  'use strict';

  let sseConnection = null; // Keep track of active SSE connection
  let currentAnalysisResults = null; // Store results globally within the module
  
  // Move these to module scope so they're available in SSE event handlers
  let pathnameHash = null;
  let currentContentHash = null;
  
  // Subtask tracking for progress calculation
  let totalSubtasks = 0;
  let completedSubtasks = 0;
  let subtaskProgress = new Map(); // Track individual subtask progress

  // Queue Status Tracking
  let currentQueueStatus = null;
  let queueStatusElement = null;

  // Initialize Queue Status UI
  function initializeQueueStatusUI() {
    // Listen for queue status updates
    VAE.events.on('queue-status-update', handleQueueStatusUpdate);
    VAE.events.on('consolidated-queue-update', handleQueueStatusUpdate);
  }

  function handleQueueStatusUpdate(data) {
    console.log('VAE.Core: Queue status update received:', data);
    currentQueueStatus = data;
    updateQueueStatusUI(data);
  }

  function updateQueueStatusUI(status) {
    // Create or update queue status element
    if (!queueStatusElement) {
      createQueueStatusElement();
    }

    if (!queueStatusElement) return;

    const statusText = queueStatusElement.querySelector('.vae-queue-status-text');
    const progressBar = queueStatusElement.querySelector('.vae-queue-progress-bar');
    const progressFill = queueStatusElement.querySelector('.vae-queue-progress-fill');
    const positionText = queueStatusElement.querySelector('.vae-queue-position');
    const waitTimeText = queueStatusElement.querySelector('.vae-queue-wait-time');

    if (status.status === 'queued' || status.status === 'pending') {
      queueStatusElement.style.display = 'block';
      
      if (statusText) {
        statusText.textContent = status.status === 'queued' ? 'In Warteschlange...' : 'Wird verarbeitet...';
      }
      
      if (positionText && status.position) {
        positionText.textContent = `Position: ${status.position}`;
        positionText.style.display = 'block';
      }
      
      if (waitTimeText && status.waitTime) {
        waitTimeText.textContent = `Geschätzte Wartezeit: ${status.waitTime}`;
        waitTimeText.style.display = 'block';
      }
      
      // Update progress bar based on poll count
      if (progressFill && status.pollCount) {
        const progress = Math.min((status.pollCount / 60) * 100, 95); // Max 95% until completion
        progressFill.style.width = `${progress}%`;
      }
      
    } else if (status.status === 'processing') {
      if (statusText) {
        statusText.textContent = 'Analyse läuft...';
      }
      if (positionText) {
        positionText.style.display = 'none';
      }
      if (progressFill) {
        progressFill.style.width = '90%';
      }
      
    } else if (status.status === 'completed') {
      // Hide queue status when completed
      setTimeout(() => {
        if (queueStatusElement) {
          queueStatusElement.style.display = 'none';
        }
      }, 2000);
      
      if (statusText) {
        statusText.textContent = 'Analyse abgeschlossen!';
      }
      if (progressFill) {
        progressFill.style.width = '100%';
      }
      
    } else if (status.status === 'failed') {
      if (statusText) {
        statusText.textContent = 'Analyse fehlgeschlagen';
        statusText.style.color = '#ef4444';
      }
      if (progressFill) {
        progressFill.style.backgroundColor = '#ef4444';
        progressFill.style.width = '100%';
      }
    }
  }

  function createQueueStatusElement() {
    // Check if element already exists
    if (document.getElementById('vae-queue-status')) {
      queueStatusElement = document.getElementById('vae-queue-status');
      return;
    }

    // Create queue status element
    queueStatusElement = document.createElement('div');
    queueStatusElement.id = 'vae-queue-status';
    queueStatusElement.className = 'vae-queue-status';
    queueStatusElement.style.display = 'none';
    
    queueStatusElement.innerHTML = `
      <div class="vae-queue-status-content">
        <div class="vae-queue-status-text">Analyse wird vorbereitet...</div>
        <div class="vae-queue-progress-bar">
          <div class="vae-queue-progress-fill"></div>
        </div>
        <div class="vae-queue-details">
          <div class="vae-queue-position" style="display: none;"></div>
          <div class="vae-queue-wait-time" style="display: none;"></div>
        </div>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .vae-queue-status {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        z-index: 10000;
        min-width: 280px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
      }
      
      .vae-queue-status-content {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      
      .vae-queue-status-text {
        font-weight: 500;
        color: #374151;
      }
      
      .vae-queue-progress-bar {
        width: 100%;
        height: 4px;
        background-color: #e5e7eb;
        border-radius: 2px;
        overflow: hidden;
      }
      
      .vae-queue-progress-fill {
        height: 100%;
        background-color: #3b82f6;
        border-radius: 2px;
        transition: width 0.3s ease;
        width: 0%;
      }
      
      .vae-queue-details {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      
      .vae-queue-position,
      .vae-queue-wait-time {
        font-size: 12px;
        color: #6b7280;
      }
      
      @media (max-width: 640px) {
        .vae-queue-status {
          top: 10px;
          right: 10px;
          left: 10px;
          min-width: auto;
        }
      }
    `;
    
    document.head.appendChild(style);
    document.body.appendChild(queueStatusElement);
  }

  // Actual fix application logic
  function applyAccessibilityFixes(issues) {
    if (!issues || issues.length === 0) {
      console.log('VAE.Core: No issues to apply fixes for.');
      VAE.AnalysisDisplay.renderFixSummary(0, 0, []); // Show an empty summary or specific message
      return;
    }

    console.log('VAE.Core: Processing', issues.length, 'issues for fix application...');
    
    // T-006.2.1: Enhanced logging for Multi-Criterion-Issue-Support
    const consolidatedIssues = issues.filter(issue => Array.isArray(issue.wcagCriterion));
    const axeCoreIssues = issues.filter(issue => issue.axeCoreSource === true);
    const llmIssues = issues.filter(issue => issue.axeCoreSource === false);
    const relatedBatches = issues.filter(issue => issue.relatedIssues && issue.relatedIssues.length > 0);
    
    console.log(`VAE.Core: Issue analysis - Multi-criterion: ${consolidatedIssues.length}, Axe-core: ${axeCoreIssues.length}, LLM-found: ${llmIssues.length}, Batch-eligible: ${relatedBatches.length}`);

    // T-006.2.4: Group related issues for batch processing
    const processedIssues = new Set();
    const batchGroups = [];
    const standaloneIssues = [];

    issues.forEach(issue => {
      if (processedIssues.has(issue)) return;
      
      if (issue.relatedIssues && issue.relatedIssues.length > 0) {
        // Create batch group
        const batchGroup = [issue];
        processedIssues.add(issue);
        
        // Find related issues
        issue.relatedIssues.forEach(relatedId => {
          const relatedIssue = issues.find(i => i.id === relatedId || i.vaeId === relatedId);
          if (relatedIssue && !processedIssues.has(relatedIssue)) {
            batchGroup.push(relatedIssue);
            processedIssues.add(relatedIssue);
          }
        });
        
        if (batchGroup.length > 1) {
          batchGroups.push(batchGroup);
          console.log(`VAE.Core: Created batch group with ${batchGroup.length} related issues`);
        } else {
          standaloneIssues.push(issue);
        }
      } else if (!processedIssues.has(issue)) {
        standaloneIssues.push(issue);
        processedIssues.add(issue);
      }
    });

    console.log(`VAE.Core: Organized into ${batchGroups.length} batch groups and ${standaloneIssues.length} standalone issues`);

    // Filter out issues that require manual developer intervention
    const autoFixableIssues = issues.filter(issue => {
      // Exclude issues that require manual fix
      if (issue.manual_fix_necessary === true) {
        console.log('VAE.Core: Skipping issue that requires manual fix:', getIssueDescription(issue));
        return false;
      }
      // Only include issues marked as auto-fixable
      if (issue.isAutoFixable !== true) {
        console.log('VAE.Core: Skipping non-auto-fixable issue:', getIssueDescription(issue));
        return false;
      }
      return true;
    });

    if (autoFixableIssues.length === 0) {
      console.log('VAE.Core: No auto-fixable issues found after filtering.');
      VAE.AnalysisDisplay.renderFixSummary(0, 0, []); // Show an empty summary
      return;
    }

    console.log(`VAE.Core: Applying fixes for ${autoFixableIssues.length} auto-fixable issues (${issues.length - autoFixableIssues.length} issues require manual intervention)...`);
    document.documentElement.setAttribute('vae-processing', 'fixing');
    VAE.AnalysisDisplay.showLoading('Korrekturen werden angewendet...'); // Inform user fixes are in progress

    let successCount = 0;
    let failCount = 0;
    const resultsDetails = [];

    const actionHandlers = {
      'setAttribute': VAE.DOM.addAttribute,
      'removeAttribute': VAE.DOM.removeAttribute,
      'setStyle': VAE.DOM.setStyle,
      'changeTag': VAE.DOM.changeTag,
      'setContent': VAE.DOM.setContent,
      'replaceElement': VAE.DOM.replaceElement,
      'removeElement': VAE.DOM.removeElement,
      'addElement': VAE.DOM.addElement,
      'moveElement': VAE.DOM.moveElement,
      'wrap': VAE.DOM.wrap,
      'wrapInner': VAE.DOM.wrapInner,
      'addJS': VAE.DOM.addJS
      // Ensure all VAE.DOM functions used for fixes are listed here
    };

    // T-006.2.3: Sort auto-fixable issues by priority (axeCoreSource first, then by severity)
    const sortedIssues = [...autoFixableIssues].sort((a, b) => {
      // Priority 1: Axe-core issues first (higher confidence)
      if (a.axeCoreSource && !b.axeCoreSource) return -1;
      if (!a.axeCoreSource && b.axeCoreSource) return 1;
      
      // Priority 2: Severity (critical first)
      const severityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'unknown': 4, 'N/A': 4 };
      const severityA = (typeof a.impact === 'string' ? a.impact.toLowerCase() : 'unknown');
      const severityB = (typeof b.impact === 'string' ? b.impact.toLowerCase() : 'unknown');
      return (severityOrder[severityA] ?? 4) - (severityOrder[severityB] ?? 4);
    });

    sortedIssues.forEach((issue) => {
      let success = false;
      let errorMsg = null;
      const startTime = performance.now();
      const action = issue.action;
      
      // Fix: Use actionParams.selector first (contains vaeId), fallback to issue.selector
      let effectiveSelector = '';
      
      // Priority 1: Use actionParams.selector if available (contains vaeId)
      if (issue.actionParams && issue.actionParams.selector) {
        effectiveSelector = issue.actionParams.selector;
      }
      // Priority 2: Fallback to issue.selector
      else if (issue.selector) {
        effectiveSelector = issue.selector;
      }
      // Priority 3: Try to construct from vaeId if available
      else if (issue.vaeId) {
        const elById = document.getElementById(issue.vaeId);
        if (elById) {
          effectiveSelector = `#${issue.vaeId}`;
        } else {
          console.warn(`VAE.Core: Element with vaeId '${issue.vaeId}' not found.`);
        }
      }
      
      if (!effectiveSelector && action !== 'addJS' && action !== 'addElement') { // addElement might not need a selector if params define parent
         errorMsg = 'No valid selector or vaeId provided for action: ' + action;
         console.warn(`VAE.Core: ${errorMsg}`, issue);
      } else if (!actionHandlers[action]) {
        errorMsg = `Unknown action type: ${action}`;
        console.warn(`VAE.Core: ${errorMsg} for issue`, issue);
      } else {
        try {
          const handler = actionHandlers[action];
          // Assuming VAE.DOM functions return {success: boolean, error: string} or throw on error.
          // Or they might return nothing on success and throw on error.
          // We need to standardize this or handle different return types.
          // For now, assume they return an object or throw.
          const params = issue.actionParams || {};
          let result;

          // Specific handling for VAE.DOM methods based on typical signatures
          // This is an assumption and needs to align with actual VAE.DOM implementation.
          if (action === 'removeElement') {
            result = handler(effectiveSelector);
          } else if (action === 'addJS') {
            result = handler(params.scriptURL, params.scriptBody, params.attributes);
          } else {
            // Most other DOM functions might take (selector, params/value, originalIssueContext)
            result = handler(effectiveSelector, params, issue);
          }

          if (result && typeof result.success === 'boolean') {
            success = result.success;
            errorMsg = result.error; // error should be null if success is true
          } else {
            // If DOM function doesn't return an object, assume success if no error was thrown
            success = true; 
          }
        } catch (handlerError) {
          console.error(`VAE.Core: Error executing action '${action}' on '${effectiveSelector}':`, handlerError);
          errorMsg = handlerError.message || `Error in ${action} handler.`;
          success = false;
        }
      }

      const duration = performance.now() - startTime;
      if (success) {
        successCount++;
      } else {
        failCount++;
      }
      
      // T-006.2.1 & T-006.2.2: Enhanced result details with multi-criterion support
      resultsDetails.push({
        action: action || 'N/A',
        target: effectiveSelector || (issue.actionParams?.parentElementSelector || 'N/A'), // for addElement, target might be parent
        success: success,
        error: errorMsg,
        durationMs: parseFloat(duration.toFixed(1)),
        wcagCriterion: normalizeWcagCriterion(issue.wcagCriterion), // Support both array and string format
        severity: issue.impact || 'N/A',
        vaeId: issue.vaeId || null,
        description: issue.description || 'N/A',
        axeCoreSource: issue.axeCoreSource || false, // T-006.2.3: Source tracking
        relatedIssues: issue.relatedIssues || [], // T-006.2.4: Related issues tracking
        multiCriterion: Array.isArray(issue.wcagCriterion), // Flag for UI rendering
        // explanation: issue.explanation || '' // If available on issue object
      });
    });

    console.log(`VAE.Core: Fix application complete - Success: ${successCount}, Failed: ${failCount}`);
    document.documentElement.setAttribute('vae-processing', 'complete');
    VAE.AnalysisDisplay.renderFixSummary(successCount, failCount, resultsDetails);
  }

  // T-006.2.1: Helper function to get issue description with multi-criterion support
  function getIssueDescription(issue) {
    if (issue.description) return issue.description;
    
    const wcag = normalizeWcagCriterion(issue.wcagCriterion);
    if (Array.isArray(wcag)) {
      return `WCAG ${wcag.join(', ')}`;
    }
    return wcag || 'Unknown issue';
  }

  // T-006.2.1: Helper function to normalize WCAG criterion (support both array and string)
  function normalizeWcagCriterion(wcagCriterion) {
    if (!wcagCriterion) return 'Unknown';
    if (Array.isArray(wcagCriterion)) return wcagCriterion;
    if (typeof wcagCriterion === 'string') return wcagCriterion;
    return 'Unknown';
  }

  // Helper function to check if automatic analysis is enabled
  function isAutoAnalysisEnabled() {
    // Priority 1: Use autoAnalyze if explicitly set (true or false)
    if (VAE.Config?.client?.autoAnalyze !== undefined) {
      return VAE.Config.client.autoAnalyze;
    }
    
    // Priority 2: Fall back to autoSubmitFixes for backward compatibility
    if (VAE.Config?.client?.autoSubmitFixes !== undefined) {
      console.log('VAE.Core: Using deprecated autoSubmitFixes for analysis control. Please migrate to autoAnalyze.');
      return VAE.Config.client.autoSubmitFixes;
    }
    
    // Default: enabled
    return true;
  }

  function simpleStringHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0;
    }
    return hash.toString(36);
  }

  function retrieveCachedData(pathnameHash) {
    const resultsCacheKey = `vae_analysis_results_cache_${pathnameHash}`;
    const contentHashCacheKey = `vae_content_hash_${pathnameHash}`;

    console.log(`VAE.Core: Attempting to retrieve from cache using hash: ${pathnameHash}`);
    console.log(`VAE.Core: Results cache key: ${resultsCacheKey}`);
    console.log(`VAE.Core: Content hash cache key: ${contentHashCacheKey}`);

    let analysisResults = null;
    let storedContentHash = null;
    let errorOccurred = null;

    try {
      const cachedResultsJSON = localStorage.getItem(resultsCacheKey);
      storedContentHash = localStorage.getItem(contentHashCacheKey);

      if (cachedResultsJSON) {
        try {
          analysisResults = JSON.parse(cachedResultsJSON);
        } catch (parseError) {
          console.error('VAE.Core: Error parsing cached analysis results. Removing potentially corrupted cache.', parseError);
          localStorage.removeItem(resultsCacheKey);
          localStorage.removeItem(contentHashCacheKey);
          analysisResults = null;
          errorOccurred = 'parse_error';
        }
      }
    } catch (storageError) {
      console.error('VAE.Core: Error accessing localStorage. Caching functionality may be impaired.', storageError);
      errorOccurred = 'storage_error';
      analysisResults = null;
      storedContentHash = null;
    }

    return { analysisResults, storedContentHash, errorOccurred };
  }

  // Placeholder for caching new data
  function cacheAnalysisData(pathnameHash, analysisData, newContentHash) {
    const resultsCacheKey = `vae_analysis_results_cache_${pathnameHash}`;
    const contentHashCacheKey = `vae_content_hash_${pathnameHash}`;
    try {
      localStorage.setItem(resultsCacheKey, JSON.stringify(analysisData));
      localStorage.setItem(contentHashCacheKey, newContentHash);
      console.log('VAE.Core: Analysis results and content hash cached.');
    } catch (e) {
      console.error('VAE.Core: Error caching analysis results to localStorage.', e);
      localStorage.removeItem(resultsCacheKey);
      localStorage.removeItem(contentHashCacheKey);
    }
  }

  // Placeholder for processing direct results
  function processDirectAnalysisResults(analysisData, pathnameHash, currentContentHash) {
    console.log('VAE.Core: Processing direct analysis results:', analysisData);
    currentAnalysisResults = analysisData;
    document.documentElement.setAttribute('vae-processing', 'analyzed');
    
    // Set analysis status to completed (green LED)
    if (VAE.UI && VAE.UI.setAnalysisStatus) {
      VAE.UI.setAnalysisStatus('completed');
      console.log('VAE.Core: Analysis completed - LEDs set to green');
    }
    
    VAE.AnalysisDisplay.renderInitialIssueSummary(analysisData);
    // TODO: Handle autoApplyFixes (i.e., call applyAccessibilityFixes if VAE.Config.client.autoApplyFixes is true)
    if (VAE.Config && VAE.Config.client && VAE.Config.client.autoApplyFixes === true) {
      if (analysisData && analysisData.issues && analysisData.issues.length > 0) {
         applyAccessibilityFixes(analysisData.issues);
      } else if (analysisData && analysisData.fixes && analysisData.fixes.length > 0) { // From original core.js.bak structure
         // Assuming `fixes` array has same structure as `issues` for applying.
         // Or adapt `applyAccessibilityFixes` to handle this structure if different.
         applyAccessibilityFixes(analysisData.fixes);
      }
    }
    // TODO: VAE.Testing.reportResultsToParent if in test environment
    if (pathnameHash && currentContentHash) {
       cacheAnalysisData(pathnameHash, analysisData, currentContentHash);
    }
  }

  async function fetchAndProcessAnalysisData(pathnameHash, currentContentHash, isManualTrigger = false) {
    console.log('VAE.Core: Starting analysis process with hash:', pathnameHash);
    
    // Prevent multiple simultaneous analyses
    if (document.documentElement.getAttribute('vae-processing') === 'processing') {
      console.warn('VAE.Core: Analysis already in progress, skipping.');
      return;
    }
    
    // Set initial processing state
    document.documentElement.setAttribute('vae-processing', 'analyzing');
    
    // Set analysis status to in-progress (blinking LED)
    if (VAE.UI && VAE.UI.setAnalysisStatus) {
      VAE.UI.setAnalysisStatus('in-progress');
      console.log('VAE.Core: Analysis started - LEDs set to in-progress');
    }
    
    VAE.AnalysisDisplay.showLoading('Analyse wird vorbereitet...'); // Initial loading message

    // Check if automatic analysis is disabled
    const autoAnalyzeEnabled = isAutoAnalysisEnabled();
    
    if (!isManualTrigger && !autoAnalyzeEnabled) {
      console.log('VAE.Core: Automatic analysis is disabled, skipping initial data submission.');
      document.documentElement.setAttribute('vae-processing', 'disabled');
      VAE.AnalysisDisplay.showDisabledMessage('Automatische Analyse ist deaktiviert. Klicken Sie auf "Analyse starten" um eine manuelle Analyse durchzuführen.');
      return;
    }

    try {
      // CRITICAL FIX: Generate data-vae-id attributes BEFORE capturing HTML
      // This ensures the HTML sent to wcag-task-manager contains the data-vae-id attributes
      console.log('VAE.Core: [TIMING FIX] Generating data-vae-id attributes before HTML capture...');
      console.log('VAE.Core: [TIMING FIX] Environment check:', {
        isBrowserless: navigator.webdriver || window.navigator.webdriver,
        userAgent: navigator.userAgent,
        hasVAEDOM: !!(window.VAE && window.VAE.DOM),
        hasEnsureStructureId: !!(window.VAE && window.VAE.DOM && window.VAE.DOM.ensureStructureId)
      });
      
      // Ensure all DOM elements have data-vae-id attributes
      const elementsWithoutVaeId = document.querySelectorAll('*:not([data-vae-id])');
      console.log(`VAE.Core: [TIMING FIX] Found ${elementsWithoutVaeId.length} elements without data-vae-id`);
      
      if (elementsWithoutVaeId.length > 0) {
        console.log(`VAE.Core: [TIMING FIX] Sample elements without data-vae-id:`, 
          Array.from(elementsWithoutVaeId).slice(0, 3).map(el => ({
            tagName: el.tagName,
            id: el.id,
            className: el.className,
            hasDataVaeId: el.hasAttribute('data-vae-id')
          }))
        );
        
        elementsWithoutVaeId.forEach((el, index) => {
          if (el.nodeType === 1) {
            const oldId = el.getAttribute('data-vae-id');
            VAE.DOM.ensureStructureId(el);
            const newId = el.getAttribute('data-vae-id');
            
            if (index < 3) { // Log first 3 for debugging
              console.log(`VAE.Core: [TIMING FIX] Element ${index}: ${el.tagName} - oldId: ${oldId}, newId: ${newId}`);
            }
          }
        });
      }
      
      const elementsWithVaeId = document.querySelectorAll('[data-vae-id]');
      console.log(`VAE.Core: [TIMING FIX] After generation: ${elementsWithVaeId.length} elements now have data-vae-id`);
      
      // NOW capture the HTML with data-vae-id attributes
      const pageHtml = document.documentElement.outerHTML;
      if (!pageHtml) {
        throw new Error('Could not retrieve page HTML.');
      }
      
      // Verify data-vae-id attributes are in the captured HTML
      const vaeIdMatches = pageHtml.match(/data-vae-id="[^"]*"/g);
      if (vaeIdMatches) {
        console.log(`VAE.Core: [TIMING FIX] ✅ Captured HTML contains ${vaeIdMatches.length} data-vae-id attributes`);
        console.log(`VAE.Core: [TIMING FIX] Sample data-vae-id attributes:`, vaeIdMatches.slice(0, 5));
      } else {
        console.warn(`VAE.Core: [TIMING FIX] ⚠️ Captured HTML contains NO data-vae-id attributes - this should not happen!`);
        console.warn(`VAE.Core: [TIMING FIX] HTML sample (first 500 chars):`, pageHtml.substring(0, 500));
      }

      // ===== US-001: CONTENT SIZE CHECK & SMART STRATEGY SELECTION =====
      console.log('VAE.Core: [US-001] Starting content analysis for strategy selection...');
      
      // 1. Calculate HTML size in bytes and KB
      const htmlSizeBytes = new Blob([pageHtml]).size;
      const htmlSizeKB = Math.round(htmlSizeBytes / 1024);
      
      // 2. Estimate token count (1 token ≈ 4 characters for HTML)
      // Apply HTML-specific adjustments for more accurate estimation
      const baseTokens = Math.round(pageHtml.length / 4);
      const tagCount = (pageHtml.match(/<[^>]+>/g) || []).length;
      const attributeBonus = Math.round(tagCount * 0.5); // HTML tags generate more tokens
      const whitespaceReduction = Math.round((pageHtml.match(/\s+/g) || []).length * 0.2);
      const estimatedTokens = Math.max(baseTokens + attributeBonus - whitespaceReduction, Math.round(baseTokens * 0.8));
      
      // 3. Check for testing/debug overrides
      const urlParams = new URLSearchParams(window.location.search);
      const forceChunking = urlParams.get('vae_force_chunking') === 'true';
      const forceConsolidated = urlParams.get('vae_force_consolidated') === 'true';
      const configForceStrategy = VAE.Config.strategy?.forceStrategy;
      
      // 4. Strategy decision based on thresholds (unless overridden)
      const sizeThreshold = VAE.Config.strategy?.maxConsolidatedSizeKB || 100;
      const tokenThreshold = VAE.Config.strategy?.maxConsolidatedTokens || 120000;
      const naturalShouldUseChunking = htmlSizeKB > sizeThreshold || estimatedTokens > tokenThreshold;
      
      // 5. Apply overrides for testing
      let shouldUseChunking = naturalShouldUseChunking;
      let strategyReason = htmlSizeKB > sizeThreshold ? 'size_limit' : 
                          estimatedTokens > tokenThreshold ? 'token_limit' : 
                          'within_limits';
      
      if (forceChunking) {
        shouldUseChunking = true;
        strategyReason = 'force_chunking_test';
        console.log('VAE.Core: [TESTING] Forcing chunking analysis via URL parameter');
      } else if (forceConsolidated) {
        shouldUseChunking = false;
        strategyReason = 'force_consolidated_test';
        console.log('VAE.Core: [TESTING] Forcing consolidated analysis via URL parameter');
      } else if (configForceStrategy === 'chunking') {
        shouldUseChunking = true;
        strategyReason = 'force_chunking_config';
        console.log('VAE.Core: [TESTING] Forcing chunking analysis via config');
      } else if (configForceStrategy === 'consolidated') {
        shouldUseChunking = false;
        strategyReason = 'force_consolidated_config';
        console.log('VAE.Core: [TESTING] Forcing consolidated analysis via config');
      }
      
      // 6. Determine final strategy
      const finalStrategy = shouldUseChunking ? 'chunking' : 'consolidated';
      
      // 7. Log strategy decision for monitoring
      console.log('VAE.Core: [US-001] Content analysis completed:', {
        htmlSizeKB,
        htmlSizeBytes,
        estimatedTokens,
        sizeThreshold,
        tokenThreshold,
        finalStrategy,
        strategyReason,
        exceedsSize: htmlSizeKB > sizeThreshold,
        exceedsTokens: estimatedTokens > tokenThreshold,
        naturalDecision: naturalShouldUseChunking ? 'chunking' : 'consolidated',
        isOverridden: strategyReason.includes('force_'),
        testingMode: forceChunking || forceConsolidated || !!configForceStrategy
      });
      
      // 8. Update UI with strategy-specific messaging
      if (shouldUseChunking) {
        const isTestMode = strategyReason.includes('force_');
        const message = isTestMode ? 
          'Chunk-Analyse wird gestartet (Test-Modus)...' : 
          'Intelligente Chunk-Analyse wird vorbereitet...';
        VAE.AnalysisDisplay.showLoading(message);
        
        if (isTestMode) {
          console.log('VAE.Core: [US-001] [TESTING] Chunking strategy forced for testing');
        } else {
          console.log('VAE.Core: [US-001] Large content detected - using intelligent chunking strategy');
        }
      } else {
        const isTestMode = strategyReason.includes('force_');
        const message = isTestMode ? 
          'Konsolidierte Analyse wird gestartet (Test-Modus)...' : 
          'Konsolidierte Analyse wird gestartet...';
        VAE.AnalysisDisplay.showLoading(message);
        
        if (isTestMode) {
          console.log('VAE.Core: [US-001] [TESTING] Consolidated strategy forced for testing');
        } else {
          console.log('VAE.Core: [US-001] Standard content size - using consolidated analysis');
        }
      }
      // ===== END US-001 IMPLEMENTATION =====

      // Run axe-core analysis first
      let axeResults = null;
      try {
        VAE.AnalysisDisplay.showLoading('Führe axe-core Analyse durch...');
        
        // Let axe-core integration handle DOM stability internally
        // This ensures the same stability checks as manual analysis
        console.log('VAE.Core: Starting axe-core analysis with DOM stability checks...');
        
        const axeAnalysis = await VAE.AxeIntegration.runAxeAnalysis();
        axeResults = VAE.AxeIntegration.transformAxeResults(axeAnalysis);
        console.log(`VAE.Core: axe-core found ${axeResults.length} issues`);
      } catch (axeError) {
        console.warn('VAE.Core: axe-core analysis failed, continuing without:', axeError);
        axeResults = [];
      }

      // NEW: Use detailed queue progress display instead of generic loading message
      VAE.AnalysisDisplay.showQueueProgress({
        status: 'initializing',
        statusMessage: 'Übertrage Daten zur Analyse...'
      });
      
      // ===== US-001: STRATEGY-AWARE API CALL =====
      // Use the intelligent strategy decision instead of hardcoded false
      const useConsolidatedAnalysis = finalStrategy === 'consolidated';
      
      console.log('VAE.Core: [US-001] Strategy decision:', {
        finalStrategy,
        useConsolidatedAnalysis,
        strategyReason,
        sizeBased: shouldUseChunking
      });
      
      const initialData = await VAE.API.fetchAAIInitialData({ 
        htmlContent: pageHtml, 
        pageUrl: window.location.href,
        axeResults: axeResults,
        // US-001: Use intelligent strategy decision
        useConsolidated: useConsolidatedAnalysis,
        strategyMetadata: {
          htmlSizeKB,
          estimatedTokens,
          finalStrategy,
          strategyReason,
          autoSelected: true
        },
        // NEW: Pass onUpdate callback for queue progress
        onUpdate: (status, pollCount) => {
          console.log(`VAE.Core: Queue status update ${pollCount}:`, status);
          console.log(`VAE.Core: Status object keys:`, Object.keys(status || {}));
          console.log(`VAE.Core: Status details:`, {
            status: status?.status,
            queue_position: status?.queue_position,
            estimated_wait_time: status?.estimated_wait_time,
            estimated_completion: status?.estimated_completion,
            chunk_progress: status?.chunk_progress,
            pollingMode: status?.pollingMode
          });
          
          // Update the detailed progress display with real queue data
          console.log('VAE.Core: Checking showQueueProgress availability...');
          console.log('VAE.Core: VAE object:', typeof VAE);
          console.log('VAE.Core: VAE.AnalysisDisplay:', typeof VAE?.AnalysisDisplay);
          console.log('VAE.Core: showQueueProgress function:', typeof VAE?.AnalysisDisplay?.showQueueProgress);
          
          if (VAE.AnalysisDisplay && VAE.AnalysisDisplay.showQueueProgress) {
            console.log('VAE.Core: Calling showQueueProgress with data:', {
              ...status,
              task_id: currentAnalysisResults?.taskId || currentAnalysisResults?.task_id,
              pollCount: pollCount
            });
            VAE.AnalysisDisplay.showQueueProgress({
              ...status,
              task_id: currentAnalysisResults?.taskId || currentAnalysisResults?.task_id,
              pollCount: pollCount
            });
            console.log('VAE.Core: showQueueProgress call completed');
          } else {
            console.warn('VAE.Core: showQueueProgress function not available');
            console.warn('VAE.Core: Available VAE methods:', Object.keys(VAE || {}));
            console.warn('VAE.Core: Available AnalysisDisplay methods:', Object.keys(VAE?.AnalysisDisplay || {}));
          }
        }
      });
      // ===== END US-001 STRATEGY-AWARE API CALL =====
      
      currentAnalysisResults = initialData;
      
      // DEBUG: Log the exact response structure for troubleshooting
      console.log('VAE.Core: [DEBUG] Complete API response structure:', {
        hasIssues: !!(currentAnalysisResults?.issues),
        issuesLength: currentAnalysisResults?.issues?.length || 0,
        hasTaskId: !!(currentAnalysisResults?.taskId),
        hasTask_id: !!(currentAnalysisResults?.task_id),
        taskIdValue: currentAnalysisResults?.taskId,
        task_idValue: currentAnalysisResults?.task_id,
        status: currentAnalysisResults?.status,
        allKeys: Object.keys(currentAnalysisResults || {})
      });

      // TODO: Get #vae-auto-section-content element for UI updates. This should be handled by a UI manager.
      const autoTab = document.querySelector('#vae-auto-section-content'); 
      if (!autoTab) {
         console.error('VAE.Core: Critical UI element #vae-auto-section-content not found.');
         document.documentElement.setAttribute('vae-processing', 'error');
         return;
      }

      if (currentAnalysisResults && currentAnalysisResults.issues && Array.isArray(currentAnalysisResults.issues)) {
        console.log('VAE.Core: Received direct analysis results with ' + currentAnalysisResults.issues.length + ' issues.');
        processDirectAnalysisResults(currentAnalysisResults, pathnameHash, currentContentHash);
      } else if (currentAnalysisResults && (currentAnalysisResults.taskCompleted || currentAnalysisResults.taskId || currentAnalysisResults.task_id)) {
        console.log('VAE.Core: Received task completion or task ID, checking for final results...');
        
        // Check if we have a task completion signal that needs result fetching
        if (currentAnalysisResults.taskCompleted && currentAnalysisResults.taskId) {
          const taskId = currentAnalysisResults.taskId;
          console.log(`VAE.Core: Task completed signal received for ${taskId}, fetching final results...`);
          
          try {
            const finalResults = await VAE.API.fetchTaskResults(taskId);
            if (finalResults && finalResults.issues) {
              currentAnalysisResults = finalResults;
              processDirectAnalysisResults(currentAnalysisResults, pathnameHash, currentContentHash);
            } else {
              throw new Error('Task completed but no valid results found');
            }
          } catch (fetchError) {
            console.error('VAE.Core: Failed to fetch task results:', fetchError);
            document.documentElement.setAttribute('vae-processing', 'error');
            VAE.AnalysisDisplay.showErrorMessage(`Fehler beim Abrufen der Ergebnisse: ${fetchError.message}`);
          }
        } else {
          // This should not happen anymore since fetchAAIInitialData handles all polling
          console.warn('VAE.Core: Received task ID without completion signal - this indicates a logic error');
          console.warn('VAE.Core: fetchAAIInitialData should handle all polling and return final results');
          throw new Error('Unexpected task state - polling should be handled by API layer');
        }

      } else if (currentAnalysisResults && currentAnalysisResults.cached && currentAnalysisResults.results) {
        console.log('VAE.Core: Received cached analysis results.');
        // Handle cached results from new polling system
        currentAnalysisResults = currentAnalysisResults.results;
        processDirectAnalysisResults(currentAnalysisResults, pathnameHash, currentContentHash);
      } else if (currentAnalysisResults && currentAnalysisResults.queueMetadata) {
        console.log('VAE.Core: Received queue metadata with polling results.');
        // Handle results from new polling system that include queueMetadata
        if (currentAnalysisResults.issues && Array.isArray(currentAnalysisResults.issues)) {
          processDirectAnalysisResults(currentAnalysisResults, pathnameHash, currentContentHash);
        } else {
          throw new Error('Queue polling completed but no valid results found');
        }
      } else {
        // Enhanced error logging for debugging
        console.error('VAE.Core: Invalid API response received:', {
          hasResults: !!currentAnalysisResults,
          resultType: typeof currentAnalysisResults,
          resultKeys: currentAnalysisResults ? Object.keys(currentAnalysisResults) : [],
          hasIssues: !!(currentAnalysisResults && currentAnalysisResults.issues),
          issuesType: currentAnalysisResults && currentAnalysisResults.issues ? typeof currentAnalysisResults.issues : 'undefined',
          hasTaskId: !!(currentAnalysisResults && (currentAnalysisResults.taskId || currentAnalysisResults.task_id)),
          hasTaskCompleted: !!(currentAnalysisResults && currentAnalysisResults.taskCompleted),
          hasCached: !!(currentAnalysisResults && currentAnalysisResults.cached),
          hasQueueMetadata: !!(currentAnalysisResults && currentAnalysisResults.queueMetadata)
        });
        throw new Error('Invalid initial response from API: No issues or Task ID.');
      }
    } catch (error) {
      console.error('VAE.Core: Initial analysis request failed:', error);
      document.documentElement.setAttribute('vae-processing', 'error');
      
      // Reset LED status on error
      if (VAE.UI && VAE.UI.setAnalysisStatus) {
        VAE.UI.setAnalysisStatus('idle');
        console.log('VAE.Core: Analysis failed - LEDs reset to idle state');
      }
      
      VAE.AnalysisDisplay.showErrorMessage('Fehler bei der initialen Analyse: ' + error.message);
      
      if (pathnameHash) { // Ensure pathnameHash is available
        const resultsCacheKeyToClear = `vae_analysis_results_cache_${pathnameHash}`;
        const contentHashCacheKeyToClear = `vae_content_hash_${pathnameHash}`;
        try {
          localStorage.removeItem(resultsCacheKeyToClear);
          localStorage.removeItem(contentHashCacheKeyToClear);
          console.log(`VAE.Core: Cleared cache for hash ${pathnameHash} due to fetch/processing error.`);
        } catch (storageClearError) {
          console.error('VAE.Core: Error trying to clear localStorage during error handling.', storageClearError);
        }
      }
    }
  }

  function initializeDynamicContentAnalysisAndDOMStructure() {
    console.log('VAE.Core: Initializing dynamic content analysis and DOM structure identification.');

    // Check if data-vae-id attributes were already generated during HTML capture
    const existingVaeIds = document.querySelectorAll('[data-vae-id]');
    const elementsWithoutVaeId = document.querySelectorAll('*:not([data-vae-id])');
    
    console.log(`VAE.Core: Found ${existingVaeIds.length} elements with data-vae-id, ${elementsWithoutVaeId.length} without`);
    
    if (elementsWithoutVaeId.length > 0) {
      console.log('VAE.Core: Generating data-vae-id for remaining elements...');
      // Initial pass to ensure remaining elements have IDs
      try {
          elementsWithoutVaeId.forEach(el => {
              if (el.nodeType === 1) VAE.DOM.ensureStructureId(el);
          });
          
          if (VAE.DOM && VAE.DOM.pendingSet && typeof VAE.DOM.pendingSet.clear === 'function') {
              console.log('VAE.Core: Initial DOM ID pass complete. Pending set state depends on VAE.DOM implementation.');
          }
      } catch (e) {
          console.error('VAE.Core: Error during initial DOM ID assignment:', e);
      }
    } else {
      console.log('VAE.Core: All elements already have data-vae-id attributes - skipping duplicate generation');
    }

    const scheduleBatchAnalysis = VAE.SharedUI.debounce(async () => {
      if (!VAE.DOM || !VAE.DOM.pendingSet || VAE.DOM.pendingSet.size === 0) {
        // console.log('VAE.Core: Debounced batch analysis triggered, but pendingSet is empty.');
        return;
      }
      console.log(`VAE.Core: Debounced batch analysis triggered for ${VAE.DOM.pendingSet.size} elements.`);
      try {
        const batchResults = await VAE.API.fetchBatchAnalysis(); // Assumes this uses VAE.DOM.pendingSet and clears it
        if (batchResults && batchResults.issues && batchResults.issues.length > 0) {
          console.log(`VAE.Core: Batch analysis returned ${batchResults.issues.length} issues.`);
          // applyAccessibilityFixes(batchResults.issues); // Deaktiviert: Automatische Fixes in Batch-Analyse
          // TODO: Potentially update VAE.AnalysisDisplay with new issues or fix summary for batch results
        } else {
          console.log('VAE.Core: Batch analysis returned no new issues.');
        }
      } catch (err) {
        console.error('VAE.Core: Batch Analysis Error', err);
        // TODO: Update VAE.AnalysisDisplay with batch analysis error if needed
      }
    }, 500); // Debounce period of 500ms, adjust as needed

    // const observer = new MutationObserver(mutationsList => {
    //   let newNodesFound = false;
    //   for (const mutation of mutationsList) {
    //     if (mutation.type === 'childList') {
    //       mutation.addedNodes.forEach(node => {
    //         if (node.nodeType === 1) { // Element node
    //           VAE.DOM.ensureStructureId(node); // Assign ID to the new node itself
    //           // Assign IDs to all descendant elements that don't have one yet
    //           node.querySelectorAll('*:not([data-vae-id])').forEach(child => VAE.DOM.ensureStructureId(child));
    //           newNodesFound = true;
    //         }
    //       });
    //     }
    //   }
    //   if (newNodesFound && VAE.DOM && VAE.DOM.pendingSet && VAE.DOM.pendingSet.size > 0) {
    //     scheduleBatchAnalysis();
    //   }
    // });

    // try {
    //     observer.observe(document.documentElement, { childList: true, subtree: true });
    //     console.log('VAE.Core: MutationObserver initialized for dynamic content.');
    // } catch (e) {
    //     console.error('VAE.Core: Failed to initialize MutationObserver:', e);
    // }
  }

  async function init() {
    console.log('VAE.Core: Initializing...');
  

    // Initialize Queue Status UI
    initializeQueueStatusUI();

    // WICHTIG: DOM-IDs ZUERST setzen, bevor Cache angewendet wird!
    initializeDynamicContentAnalysisAndDOMStructure();

    const pagePathname = window.location.pathname;
    pathnameHash = simpleStringHash(pagePathname);
    try {
      currentContentHash = await VAE.ContentHasher.getCurrentPageHash();
    } catch (hashError) {
      console.error('VAE.Core: Error generating content hash. Cache validation by content will be skipped.', hashError);
    }

    const cachedData = retrieveCachedData(pathnameHash);

    if (cachedData.errorOccurred) {
      console.warn(`VAE.Core: An error (${cachedData.errorOccurred}) occurred during cache retrieval. Cache will be cleared, but no automatic analysis will start.`);
      // Clear invalid cache but don't start analysis automatically
      if (pathnameHash) {
        const resultsCacheKey = `vae_analysis_results_cache_${pathnameHash}`;
        const contentHashCacheKey = `vae_content_hash_${pathnameHash}`;
        try {
          localStorage.removeItem(resultsCacheKey);
          localStorage.removeItem(contentHashCacheKey);
          console.log(`VAE.Core: Cleared invalid cache for hash ${pathnameHash}.`);
        } catch (storageClearError) {
          console.error('VAE.Core: Error clearing invalid cache.', storageClearError);
        }
      }
    } else if (cachedData.analysisResults && cachedData.storedContentHash) {
      console.log('VAE.Core: Found valid cached data. Validating content hash...');
      if (currentContentHash && currentContentHash === cachedData.storedContentHash) {
        console.log('VAE.Core: Content hash matches. Using cached analysis results.');
        currentAnalysisResults = cachedData.analysisResults;
        processDirectAnalysisResults(currentAnalysisResults, pathnameHash, currentContentHash);
      } else {
        if (!currentContentHash) {
            console.log('VAE.Core: Could not generate current content hash. Cached data will not be used automatically.');
        } else {
            console.log('VAE.Core: Content hash mismatch. Cached data is stale and will not be used automatically.');
        }
        // Clear stale cache
        if (pathnameHash) {
          const resultsCacheKey = `vae_analysis_results_cache_${pathnameHash}`;
          const contentHashCacheKey = `vae_content_hash_${pathnameHash}`;
          try {
            localStorage.removeItem(resultsCacheKey);
            localStorage.removeItem(contentHashCacheKey);
            console.log(`VAE.Core: Cleared stale cache for hash ${pathnameHash}.`);
          } catch (storageClearError) {
            console.error('VAE.Core: Error clearing stale cache.', storageClearError);
          }
        }
        
        // Check if automatic analysis should be started for stale cache
        const autoAnalyzeEnabled = isAutoAnalysisEnabled();
        
        if (autoAnalyzeEnabled) {
          console.log('VAE.Core: Automatic analysis is enabled. Starting fresh analysis due to stale cache...');
          // Start analysis automatically after a short delay to ensure UI is ready
          setTimeout(() => {
            fetchAndProcessAnalysisData(pathnameHash, currentContentHash, false);
          }, 100);
        } else {
          console.log('VAE.Core: Automatic analysis is disabled. Analysis can be started manually via button.');
        }
      }
    } else {
      console.log('VAE.Core: No cached data found.');
      
      // Check if automatic analysis should be started
      const autoAnalyzeEnabled = isAutoAnalysisEnabled();
      
      if (autoAnalyzeEnabled) {
        console.log('VAE.Core: Automatic analysis is enabled. Starting analysis...');
        // Start analysis automatically after a short delay to ensure UI is ready
        setTimeout(() => {
          fetchAndProcessAnalysisData(pathnameHash, currentContentHash, false);
        }, 100);
      } else {
        console.log('VAE.Core: Automatic analysis is disabled. Analysis can be started manually via button.');
      }
    }
    
    // Add event listener for manual analysis trigger
    window.addEventListener('vae-start-analysis', () => {
      console.log('VAE.Core: Analysis button clicked, analysis mode:', window.VAE.analysisMode);
      fetchAndProcessAnalysisData(pathnameHash, currentContentHash, true);
    });
  }

  // Testing helper functions (available in console as VAE.Core.*)
  function forceChunkingTest() {
    console.log('VAE.Core: [TESTING] Reloading page with forced chunking...');
    const url = new URL(window.location);
    url.searchParams.set('vae_force_chunking', 'true');
    url.searchParams.delete('vae_force_consolidated');
    window.location.href = url.toString();
  }
  
  function forceConsolidatedTest() {
    console.log('VAE.Core: [TESTING] Reloading page with forced consolidated analysis...');
    const url = new URL(window.location);
    url.searchParams.set('vae_force_consolidated', 'true');
    url.searchParams.delete('vae_force_chunking');
    window.location.href = url.toString();
  }
  
  function clearTestMode() {
    console.log('VAE.Core: [TESTING] Clearing test mode and reloading...');
    const url = new URL(window.location);
    url.searchParams.delete('vae_force_chunking');
    url.searchParams.delete('vae_force_consolidated');
    window.location.href = url.toString();
  }

  return {
    init: init,
    // Testing helpers
    forceChunkingTest,
    forceConsolidatedTest,
    clearTestMode
  };
})();
